import {
  EditOutlined,
  FileDoneOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Descriptions,
  Form,
  Input,
  Layout,
  message,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tooltip,
} from "antd";
import moment from "moment";
import { useCallback, useContext, useEffect, useState } from "react";
import { USER_ROLE } from "../../enums/user.enum";
import NetworkApi from "../../network/networkApi";
import UserContext from "../../providers/UserContext";
import BreadMe from "../lib/Breadme";

interface ProductItem {
  name: string;
  code: string;
  type: string;
}

interface FactoryItem {
  id: number;
  year: number;
  createdAt: string;
  organization: {
    rd: string;
    name: string;
    description: string | null;
  };
  incentive: {
    name: string;
    alias: string;
    products: ProductItem[];
  };
}

interface FactoryListResponse {
  result: {
    list: FactoryItem[];
    success: boolean;
    page: number;
    count: number;
  };
}

interface FactoryListFilter {
  rd?: string;
  name?: string;
  status?: boolean;
  year?: number;
}

interface ContentType {
  loading: boolean;
  page: number;
  count: number;
  filter?: FactoryListFilter;
  result?: FactoryItem[];
}

interface ModalStateType {
  visible: boolean;
  id?: number;
}

const FactoryList = () => {
  const { user } = useContext(UserContext);
  const [content, changeContent] = useState<ContentType>({
    loading: false,
    page: 1,
    count: 0,
    filter: {},
    result: [],
  });

  const [detailModal, changeDetailModal] = useState<ModalStateType>({
    visible: false,
  });
  const [productModal, changeProductModal] = useState<ModalStateType>({
    visible: false,
  });

  const loadContent = useCallback(async () => {
    changeContent((c) => {
      return {
        ...c,
        loading: true,
      };
    });
    let response = await NetworkApi.requestGet("/api/incentive/org/list", {
      ...content.filter,
      year: content.filter?.year
        ? moment(content.filter.year).format("YYYY")
        : undefined,
      page: content.page,
    });
    if (response.resultCode === 200) {
      changeContent((c) => {
        return {
          ...c,
          loading: false,
          page: response.result.page,
          count: response.result.count,
          result: response.result.list || [],
        };
      });
    } else {
      message.error(response.message || "Алдаа гарлаа, дахин оролдоно уу!");
    }
  }, [content]);

  useEffect(() => {
    loadContent();
  }, [content.filter, content.page]);

  const changeFilter = (filter: any) => {
    changeContent({ ...content, filter });
  };

  const getColumns = () => {
    const columns = [
      {
        title: "№",
        dataIndex: "id",
        render: (v: any, r: any, index: number) =>
          `${index + 1 + (content.page - 1) * 10}`,
      },
      {
        title: "Үйлдвэрийн нэр",
        dataIndex: "organization",
        render: (value: any) => value.name,
      },
      {
        title: "Регистр",
        dataIndex: "organization",
        render: (value: any) => value.rd,
      },
      {
        title: "Урамшуулалын төрөл",
        dataIndex: "incentive",
        render: (value: any) => value.name,
      },
      {
        title: "Үйлдвэрийн шалгах бүтээгдэхүүн",
        dataIndex: "incentive",
        render: (value: any) => {
          return (
            <Space>
              {value.products.map((p: any) => (
                <Tooltip key={p.id} placement="top" title={p.name}>
                  <span style={{ cursor: "default" }}>{p.code}</span>
                </Tooltip>
              ))}
            </Space>
          );
        },
      },
      {
        title: "Хүчинтэй хугацаа",
        dataIndex: "year",
      },
      {
        title: "Идэвхитэй эсэх",
        dataIndex: "isActive",
        render: (value: any) => (value ? "Идэвхитэй" : "Идэвхигүй"),
      },
      ...(user.role?.cd === USER_ROLE.ADMIN
        ? [
            {
              title: "Үйлдэл",
              dataIndex: "id",
              width: "100px",
              render: (value: any) => (
                <Space size={16}>
                  <Button
                    type={"primary"}
                    size={"small"}
                    ghost
                    icon={<EditOutlined />}
                    onClick={() =>
                      changeDetailModal({ visible: true, id: value })
                    }
                  />
                </Space>
              ),
            },
          ]
        : []),
    ];
    return columns;
  };

  return (
    <>
      <BreadMe title={["Боловсруулах үйлдвэр"]} />
      <Layout.Content
        style={{ background: "#fff", padding: 24, margin: 0, minHeight: 280 }}
      >
        <Row>
          <Col span={24}>
            <Form
              layout="inline"
              onFinish={(filter: any) => {
                if (filter.targetDate) {
                  filter.targetDate = filter.targetDate.format("YYYY-MM-DD");
                }
                changeFilter(filter);
              }}
            >
              <Row gutter={[0, 16]} style={{ width: "100%" }}>
                <Col flex="none">
                  <Form.Item name="name">
                    <Input placeholder={"Үйлдвэрийн нэр"} />
                  </Form.Item>
                </Col>
                <Col flex="none">
                  <Form.Item name="rd">
                    <Input placeholder={"Регистр"} />
                  </Form.Item>
                </Col>
                <Col flex="200px">
                  <Form.Item>
                    <Select placeholder="Урамшуулалын төрөл" allowClear>
                      <Select.Option value="vegetable">
                        Хүнсний ногоо
                      </Select.Option>
                      <Select.Option value="wheat">Улаанбуудай</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col flex="200px">
                  <Form.Item name="status">
                    <Select placeholder="Төлөв" allowClear>
                      <Select.Option value="">Бүгд</Select.Option>
                      <Select.Option value="true">Идэвхитэй</Select.Option>
                      <Select.Option value="false">Идэвхигүй</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col flex="none">
                  <Form.Item name="year">
                    <DatePicker picker="year" placeholder="Он" />
                  </Form.Item>
                </Col>
                <Col flex="none">
                  <Button
                    htmlType="submit"
                    type={"primary"}
                    style={{ marginRight: 16 }}
                  >
                    <SearchOutlined />
                  </Button>
                </Col>
                <Col flex="none">
                  <Button
                    type={"primary"}
                    onClick={() =>
                      changeDetailModal({
                        visible: true,
                      })
                    }
                    style={{ marginRight: 16 }}
                  >
                    <PlusOutlined />
                    Нэмэх
                  </Button>
                </Col>
                <Col flex="none">
                  <Button
                    type={"primary"}
                    onClick={() =>
                      changeProductModal({
                        visible: true,
                      })
                    }
                  >
                    <FileDoneOutlined />
                    Урамшуулал олгох бүтээгдэхүүн
                  </Button>
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
        <Row style={{ marginTop: 15, marginBottom: 15 }}>
          <Col span={24}>
            <Descriptions title="" style={{ marginTop: 15 }}>
              <Descriptions.Item label="Нийт">
                {content.count}
              </Descriptions.Item>
            </Descriptions>
          </Col>
          <Col span={24}>
            <Table
              scroll={{ x: true }}
              style={{ width: "100%" }}
              pagination={{
                current: content.page,
                total: content.count,
              }}
              onChange={(p) => {
                changeContent({
                  ...content,
                  page: p.current || 1,
                });
              }}
              loading={content.loading}
              columns={getColumns()}
              dataSource={content.result ?? []}
              rowKey={"id"}
            />
          </Col>
        </Row>
        {detailModal.visible && (
          <FactoryModal
            id={detailModal.id}
            visible={detailModal.visible}
            onCancel={() => {
              loadContent();
              changeDetailModal({ visible: false });
            }}
          />
        )}
        {productModal.visible && (
          <ProductModal
            visible={productModal.visible}
            onCancel={() => changeProductModal({ visible: false })}
          />
        )}
      </Layout.Content>
    </>
  );
};

export default FactoryList;

interface FactoryDetailType {
  org: {
    id: number;
    year: number;
    createdAt: string;
    organization: {
      rd: string;
      name: string;
      description: string | null;
    };
    incentive: {
      name: string;
      alias: string;
    };
    isActive: boolean;
  };
  success: boolean;
}

interface FactoryDetailResponse {
  result: FactoryDetailType;
}

interface DetailContentType {
  loading: boolean;
  result?: FactoryDetailType;
}

interface ModalProps {
  visible: boolean;
  id?: any;
  onCancel: () => void;
}

const FactoryModal = ({ visible, id, onCancel }: ModalProps) => {
  const [form] = Form.useForm();

  const [content, changeContent] = useState<DetailContentType>({
    loading: false,
  });

  const loadContent = useCallback(async () => {
    changeContent((c) => {
      return {
        ...c,
        loading: true,
      };
    });
    let response = await NetworkApi.requestGet(
      `/api/incentive/org/detail/${id}`
    );
    if (response.resultCode === 200) {
      changeContent((c) => {
        return {
          ...c,
          loading: false,
          result: response.result,
        };
      });
    } else {
      message.error(response.message || "Алдаа гарлаа, дахин оролдоно уу!");
    }
  }, [content, id]);

  useEffect(() => {
    if (visible && id) {
      loadContent();
    }
  }, [id]);

  useEffect(() => {
    if (visible && content.result) {
      form.setFieldsValue({
        name: content.result.org.organization.name,
        rd: content.result.org.organization.rd,
        year: moment(content.result.org.year),
        status: content.result.org.isActive,
        incentiveType: content.result.org.incentive.alias,
      });
    }
  }, [content, id, visible]);

  const onFinish = useCallback(async (values: any) => {
    console.log(values);
    let response = await NetworkApi.requestPost("/api/incentive/org/update", {
      ...values,
      year: moment(values.year).format("YYYY"),
      status: values.status ? true : false,
    });
    if (response.resultCode === 200) {
      message.success("Амжилттай хадгалагдлаа");
      onCancel();
    } else {
      message.error(response.message || "Алдаа гарлаа, дахин оролдоно уу!");
    }
  }, []);

  return (
    <Modal
      width={1000}
      visible={visible}
      title={id ? "Үйлдвэрийн дэлгэрэнгүй" : "Үйлдвэр нэмэх"}
      onCancel={onCancel}
      footer={null}
    >
      <Row>
        <Col span={24}>
          <Form
            wrapperCol={{ span: 14 }}
            labelCol={{ span: 8 }}
            form={form}
            onFinish={onFinish}
          >
            <Form.Item
              name="name"
              label="Үйлдвэрийн нэр"
              required
              rules={[{ required: true, message: "Үйлдвэрийн нэр оруулна уу" }]}
            >
              <Input placeholder="Үйлдвэрийн нэр оруулна уу" />
            </Form.Item>
            <Form.Item
              label="Регистр"
              name="rd"
              required
              rules={[
                { required: true, message: "Регистр оруулна уу" },
                () => ({
                  validator(rule) {
                    const rd = form.getFieldValue("rd");
                    if (rd && `${rd}`.length !== 7) {
                      return Promise.reject("Регистр 7 оронтой байх ёстой");
                    }
                    if (rd && isNaN(Number(rd))) {
                      return Promise.reject("Зөвхөн тоо оруулна уу");
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <Input placeholder="Регистр оруулна уу" />
            </Form.Item>
            <Form.Item
              name="incentiveType"
              label="Урамшуулалын төрөл"
              required
              rules={[
                { required: true, message: "Урамшуулалын төрөл сонгоно уу" },
              ]}
            >
              <Select placeholder="Урамшуулалын төрөл сонгоно уу">
                <Select.Option value="vegetable">Хүнсний ногоо</Select.Option>
                <Select.Option value="wheat">Улаанбуудай</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item
              name="year"
              label="Хүчинтэй хугацаа"
              required
              rules={[
                { required: true, message: "Хүчинтэй хугацаа сонгоно уу" },
              ]}
            >
              <DatePicker picker="year" placeholder="Он" />
            </Form.Item>
            <Form.Item
              name="status"
              label="Идэвхитэй эсэх"
              valuePropName="checked"
            >
              <Checkbox
                onChange={(e) => {
                  form.setFieldsValue({
                    status: e.target.checked,
                  });
                }}
              >
                Идэвхитэй
              </Checkbox>
            </Form.Item>
            <Button type="primary" htmlType="submit">
              Хадгалах
            </Button>
          </Form>
        </Col>
      </Row>
    </Modal>
  );
};

interface ProductItemType {
  name: string;
  code: string;
  type: string;
  incentive: {
    id: number;
    name: string;
    alias: string;
  };
}

interface ProductListResponse {
  result: {
    list: ProductItemType[];
    success: boolean;
    count: number;
    page: number;
  };
}

interface ProductContentType {
  loading: boolean;
  result: ProductItemType[];
  count: number;
  page: number;
}

const ProductModal = ({ visible, onCancel }: ModalProps) => {
  const [content, changeContent] = useState<ProductContentType>({
    loading: true,
    result: [],
    count: 0,
    page: 1,
  });

  const loadContent = useCallback(async () => {
    changeContent((c) => {
      return {
        ...c,
        loading: true,
      };
    });
    let response = await NetworkApi.requestGet(`/api/incentive/product/list`, {
      page: content.page,
    });
    if (response.resultCode === 200) {
      changeContent((c) => {
        return {
          ...c,
          loading: false,
          page: response.result.page,
          count: response.result.count,
          result: response.result.list || [],
        };
      });
    } else {
      message.error(response.message || "Алдаа гарлаа, дахин оролдоно уу!");
    }
  }, [content]);

  useEffect(() => {
    loadContent();
  }, [content.page, visible]);

  const columns = [
    {
      title: "№",
      dataIndex: "id",
      render: (v: any, r: any, index: number) =>
        `${index + 1 + (content.page - 1) * 10}`,
    },
    {
      title: "Код",
      dataIndex: "code",
    },
    {
      title: "Бүтээгдэхүүн",
      dataIndex: "name",
    },
    {
      title: "Урамшуулалын төрөл",
      dataIndex: "incentive",
      render: (value: any) => value.name,
    },
  ];
  return (
    <Modal
      visible={visible}
      title="Урамшуулал олгох бүтээгдэхүүн"
      onCancel={onCancel}
      footer={null}
      width={1000}
    >
      <Table
        scroll={{ x: true }}
        style={{ width: "100%" }}
        pagination={{
          current: content.page,
          total: content.count,
        }}
        onChange={(p) => {
          changeContent({
            ...content,
            page: p.current || 1,
          });
        }}
        loading={content.loading}
        columns={columns}
        dataSource={content.result}
        rowKey={"id"}
      />
    </Modal>
  );
};
